/** Session-only recovery references. No address, body, draft or source details. */
export interface MessagingSendAttempt {
  actorStaffUserId: string;
  contactId: string;
  organisationId: string | null;
  requestKey: string;
}

/** Missing or malformed current identity never acquires another actor's custody. */
export const resolveMessagingSendActorId = (
  staffUser: unknown,
): string | null =>
  staffUser !== null &&
  typeof staffUser === "object" &&
  "id" in staffUser &&
  typeof staffUser.id === "string"
    ? staffUser.id
    : null;
type SessionStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;
export type SendAttemptRead =
  | { state: "none" }
  | { state: "held"; attempt: MessagingSendAttempt }
  | { state: "unavailable" };
export const MESSAGING_SEND_CUSTODY_KEY = "hc.admin.messaging.send-custody.v1";
const uuid = (value: unknown): value is string =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
const valid = (value: unknown): value is MessagingSendAttempt => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return (
    Object.keys(row).sort().join(",") ===
      "actorStaffUserId,contactId,organisationId,requestKey" &&
    uuid(row.actorStaffUserId) &&
    uuid(row.contactId) &&
    (row.organisationId === null || uuid(row.organisationId)) &&
    typeof row.requestKey === "string" &&
    row.requestKey.length <= 256 &&
    /^email-assistant:[a-z0-9:-]+$/i.test(row.requestKey)
  );
};
const session = (): SessionStore => window.sessionStorage;
const load = (storage: SessionStore): MessagingSendAttempt[] => {
  const raw = storage.getItem(MESSAGING_SEND_CUSTODY_KEY);
  if (raw === null) return [];
  if (raw.length > 20000) throw new Error("invalid_custody");
  const parsed: unknown = JSON.parse(raw);
  if (
    !Array.isArray(parsed) ||
    parsed.length > 32 ||
    !parsed.every(valid) ||
    new Set(parsed.map((row) => `${row.actorStaffUserId}:${row.contactId}`))
      .size !== parsed.length
  )
    throw new Error("invalid_custody");
  return parsed;
};

/** Actor changes cannot disclose or restore another operator's references. */
export const readMessagingSendAttempt = (
  actorStaffUserId: string,
  contactId: string,
  storage?: SessionStore,
): SendAttemptRead => {
  try {
    if (!uuid(actorStaffUserId) || !uuid(contactId))
      return { state: "unavailable" };
    const target = storage ?? session();
    const rows = load(target);
    if (rows.some((row) => row.actorStaffUserId !== actorStaffUserId)) {
      target.removeItem(MESSAGING_SEND_CUSTODY_KEY);
      return { state: "none" };
    }
    const attempt = rows.find((row) => row.contactId === contactId);
    return attempt ? { state: "held", attempt } : { state: "none" };
  } catch {
    return { state: "unavailable" };
  }
};

/** Persist before the request. Failure keeps dispatch disabled. */
export const retainMessagingSendAttempt = (
  attempt: MessagingSendAttempt,
  storage?: SessionStore,
): boolean => {
  try {
    if (!valid(attempt)) return false;
    const target = storage ?? session();
    const current = readMessagingSendAttempt(
      attempt.actorStaffUserId,
      attempt.contactId,
      target,
    );
    if (current.state !== "none") return false;
    const rows = [...load(target), attempt];
    if (rows.length > 32) return false;
    target.setItem(MESSAGING_SEND_CUSTODY_KEY, JSON.stringify(rows));
    const retained = readMessagingSendAttempt(
      attempt.actorStaffUserId,
      attempt.contactId,
      target,
    );
    return (
      retained.state === "held" &&
      retained.attempt.requestKey === attempt.requestKey &&
      retained.attempt.organisationId === attempt.organisationId
    );
  } catch {
    return false;
  }
};

/** Clear only the original attempt after a definite refusal or known outcome. */
export const clearMessagingSendAttempt = (
  attempt: MessagingSendAttempt,
  storage?: SessionStore,
): void => {
  try {
    const target = storage ?? session();
    const rows = load(target).filter(
      (row) =>
        !(
          row.actorStaffUserId === attempt.actorStaffUserId &&
          row.contactId === attempt.contactId &&
          row.organisationId === attempt.organisationId &&
          row.requestKey === attempt.requestKey
        ),
    );
    if (rows.length)
      target.setItem(MESSAGING_SEND_CUSTODY_KEY, JSON.stringify(rows));
    else target.removeItem(MESSAGING_SEND_CUSTODY_KEY);
  } catch {
    /* Unknown/corrupt storage never grants authority to send. */
  }
};

export const clearMessagingSendCustody = (): void => {
  try {
    session().removeItem(MESSAGING_SEND_CUSTODY_KEY);
  } catch {
    /* No data or dispatch authority. */
  }
};

/** Manual jobs have no contact identity. Keep only the initiating actor/job/scope/key. */
export interface MessagingManualSendAttempt {
  actorStaffUserId: string;
  jobId: string;
  organisationId: string;
  requestKey: string;
}
export const MESSAGING_MANUAL_SEND_CUSTODY_KEY =
  "hc.admin.messaging.manual-send-custody.v1";
export type ManualSendAttemptRead =
  | { state: "none" }
  | { state: "held"; attempt: MessagingManualSendAttempt }
  | { state: "unavailable" };
const validManualAttempt = (
  value: unknown,
): value is MessagingManualSendAttempt => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return (
    Object.keys(row).sort().join(",") ===
      "actorStaffUserId,jobId,organisationId,requestKey" &&
    uuid(row.actorStaffUserId) &&
    uuid(row.jobId) &&
    uuid(row.organisationId) &&
    typeof row.requestKey === "string" &&
    row.requestKey.length <= 256 &&
    /^email-assistant:[a-z0-9:-]+$/i.test(row.requestKey)
  );
};
const loadManualAttempts = (
  storage: SessionStore,
): MessagingManualSendAttempt[] => {
  const raw = storage.getItem(MESSAGING_MANUAL_SEND_CUSTODY_KEY);
  if (raw === null) return [];
  if (raw.length > 20000) throw new Error("invalid_manual_custody");
  const parsed: unknown = JSON.parse(raw);
  if (
    !Array.isArray(parsed) ||
    parsed.length > 32 ||
    !parsed.every(validManualAttempt) ||
    new Set(parsed.map((row) => row.actorStaffUserId)).size !== parsed.length
  )
    throw new Error("invalid_manual_custody");
  return parsed;
};
/** An actor's unresolved job blocks replacement manual work across sources/organisations.
 * Other actors' references are neither exposed nor erased by a reader. */
export const readMessagingManualSendAttempt = (
  actorStaffUserId: string,
  storage?: SessionStore,
): ManualSendAttemptRead => {
  try {
    if (!uuid(actorStaffUserId)) return { state: "unavailable" };
    const attempt = loadManualAttempts(storage ?? session()).find(
      (row) => row.actorStaffUserId === actorStaffUserId,
    );
    return attempt ? { state: "held", attempt } : { state: "none" };
  } catch {
    return { state: "unavailable" };
  }
};
/** Retain and verify before dispatch. Never replace an original, even for the same key. */
export const retainMessagingManualSendAttempt = (
  attempt: MessagingManualSendAttempt,
  storage?: SessionStore,
): boolean => {
  try {
    if (!validManualAttempt(attempt)) return false;
    const target = storage ?? session();
    if (
      readMessagingManualSendAttempt(attempt.actorStaffUserId, target).state !==
      "none"
    )
      return false;
    const rows = [...loadManualAttempts(target), attempt];
    if (rows.length > 32) return false;
    target.setItem(MESSAGING_MANUAL_SEND_CUSTODY_KEY, JSON.stringify(rows));
    const saved = readMessagingManualSendAttempt(
      attempt.actorStaffUserId,
      target,
    );
    return (
      saved.state === "held" &&
      saved.attempt.jobId === attempt.jobId &&
      saved.attempt.organisationId === attempt.organisationId &&
      saved.attempt.requestKey === attempt.requestKey
    );
  } catch {
    return false;
  }
};
/** Release only an exact original with a definite refusal or checked terminal outcome. */
export const clearMessagingManualSendAttempt = (
  attempt: MessagingManualSendAttempt,
  storage?: SessionStore,
): boolean => {
  try {
    const target = storage ?? session();
    const rows = loadManualAttempts(target).filter(
      (row) =>
        !(
          row.actorStaffUserId === attempt.actorStaffUserId &&
          row.jobId === attempt.jobId &&
          row.organisationId === attempt.organisationId &&
          row.requestKey === attempt.requestKey
        ),
    );
    if (rows.length)
      target.setItem(MESSAGING_MANUAL_SEND_CUSTODY_KEY, JSON.stringify(rows));
    else target.removeItem(MESSAGING_MANUAL_SEND_CUSTODY_KEY);
    return (
      readMessagingManualSendAttempt(attempt.actorStaffUserId, target).state ===
      "none"
    );
  } catch {
    return false;
  }
};
